import type React from "react";
import { useState } from "react";

import { faTrophy } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useGetPerformanceSummary } from "api/services/admin/hooks";
import { PieChart } from "shared/atoms/Charts";
import { MUISelect } from "shared/atoms/MUI";
import { WidgetBox } from "shared/containers/WidgetBox";
import { BLUE, DARK_BLUE, GREEN, PURPLE, TEAL, YELLOW } from "theme/colors";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { getStartOfMonth } from "utils/date";
import { translateCurrency } from "utils/translations";

import { SummaryBox } from "../EnrollmentGoals/SummaryBox";
import { NoGraphData } from "../NoGraphData";
import { useMonthOptions } from "../utils";

import { PerformanceSummarySkeleton } from "./PerformanceSummarySkeleton";

interface PerformanceSummaryProps {
  hcoID: number;
}

export const PerformanceSummary: React.FC<PerformanceSummaryProps> = ({ hcoID }) => {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(getStartOfMonth());
  const { monthOptions } = useMonthOptions();

  const { data: performanceSummaryData, isLoading } = useGetPerformanceSummary({
    health_care_organization_id: hcoID,
    params: {
      month: selectedMonth,
    },
  });

  const enrollmentPercentage = performanceSummaryData?.enrollment_goal
    ? Math.round((performanceSummaryData.total_enrolled / performanceSummaryData.enrollment_goal) * 100)
    : 0;

  const enrolledValue = performanceSummaryData?.total_enrolled ?? 0;
  const remainingValue = Math.max((performanceSummaryData?.enrollment_goal ?? 0) - enrolledValue, 0);

  const hasData = !!performanceSummaryData;

  const pieData = [
    { name: t("admin.performance_summary.total_enrolled"), value: enrolledValue, fill: GREEN[100] },
    { name: t("admin.performance_summary.enrollments_remaining"), value: remainingValue, fill: DARK_BLUE[10] },
  ];

  return (
    <WidgetBox
      title={t("admin.performance_summary.title")}
      icon={<FontAwesomeIcon icon={faTrophy} />}
      color="yellow"
      toolbar={
        <MUISelect
          size="small"
          options={monthOptions}
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value as string)}
          sx={{ minWidth: 160 }}
        />
      }
      skipPadding
    >
      {isLoading && <PerformanceSummarySkeleton />}
      {!isLoading && !hasData && <NoGraphData title={t("admin.performance_summary.no_data")} />}
      {!isLoading && hasData && (
        <Stack direction="row" spacing={4} alignItems="center">
          <PieChart
            data={pieData}
            height={300}
            label={
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="h2">{enrollmentPercentage}%</Typography>
                <Typography variant="label" color={DARK_BLUE[60]}>
                  {t("admin.performance_summary.total_enrolled")}
                </Typography>
              </Stack>
            }
          />
          <Stack spacing={1} flex="1 1 120px">
            <SummaryBox
              label={t("admin.performance_summary.total_enrolled")}
              value={performanceSummaryData?.total_enrolled ?? 0}
              backgroundColor={BLUE[10]}
              valueColor={BLUE[100]}
              valueTypographyVariant="h3"
            />
            <SummaryBox
              label={t("admin.performance_summary.enrollment_goal")}
              value={performanceSummaryData?.enrollment_goal ?? 0}
              backgroundColor={GREEN[10]}
              valueColor={GREEN[100]}
              valueTypographyVariant="h3"
            />
            <SummaryBox
              label={t("admin.performance_summary.total_earnings_cents")}
              value={translateCurrency(
                convertCentsToDollarsNumber(performanceSummaryData?.total_earnings_cents ?? 0),
                performanceSummaryData?.currency
              )}
              backgroundColor={PURPLE[10]}
              valueColor={PURPLE[100]}
              valueTypographyVariant="h3"
            />
            <SummaryBox
              label={t("admin.performance_summary.total_commission_cents")}
              value={translateCurrency(
                convertCentsToDollarsNumber(performanceSummaryData?.total_commission_cents ?? 0),
                performanceSummaryData?.currency
              )}
              backgroundColor={TEAL[10]}
              valueColor={TEAL[100]}
              valueTypographyVariant="h3"
            />
            <SummaryBox
              label={t("admin.performance_summary.total_goals_reached")}
              value={`${performanceSummaryData?.total_goals_reached ?? 0} / ${
                performanceSummaryData?.total_goals ?? 0
              }`}
              backgroundColor={YELLOW[10]}
              valueColor={YELLOW[100]}
              valueTypographyVariant="h3"
            />
            <SummaryBox
              label={t("admin.performance_summary.total_bonus_cents")}
              value={translateCurrency(
                convertCentsToDollarsNumber(performanceSummaryData?.total_bonus_cents ?? 0),
                performanceSummaryData?.currency
              )}
              backgroundColor={GREEN[10]}
              valueColor={GREEN[100]}
              valueTypographyVariant="h3"
            />
          </Stack>
        </Stack>
      )}
    </WidgetBox>
  );
};
